(function () {
  'use strict';

  /*
   * These are editorial directions built from the current catalogue. They
   * are deliberately not presented as measurements, forecasts or care
   * guarantees. Links are kept as plain, same-origin routes so the candidate
   * can be checked against the dated Shopify snapshot before release.
   */
  var MODES = {
    disco: {
      kicker: 'Disco decor',
      heading: 'Find your disco starting point',
      intro: 'Choose what feels right and we will point you to a few current pieces to compare.',
      questions: [
        {
          id: 'mood',
          label: 'What should the piece do in the room?',
          options: [
            { id: 'statement', title: 'Make the room stop', desc: 'A focal point with presence' },
            { id: 'quiet', title: 'Catch the eye quietly', desc: 'A smaller glint to discover' },
            { id: 'playful', title: 'Start a conversation', desc: 'A piece with a sense of fun' }
          ]
        },
        {
          id: 'job',
          label: 'What job should it have?',
          options: [
            { id: 'wall', title: 'Live on the wall', desc: 'A sculptural focal point' },
            { id: 'plant', title: 'Hold a plant', desc: 'A planter with reflective detail' },
            { id: 'party', title: 'Join a bar or party setup', desc: 'A piece for hosting' }
          ]
        },
        {
          id: 'place',
          label: 'Where will you notice it most?',
          options: [
            { id: 'living', title: 'Living room', desc: 'The room everyone uses' },
            { id: 'shelf', title: 'Shelf or console', desc: 'A closer, smaller view' },
            { id: 'bar', title: 'Kitchen or bar area', desc: 'Where people gather' }
          ]
        }
      ],
      outcomes: {
        statement: {
          title: 'A statement piece',
          copy: 'Start with a sculptural wall piece or a larger focal point with presence in the room.',
          links: [
            { label: 'The Disco Era mirror ball bust', href: '/products/the-disco-era-mirror-ball-bust-wall-art' },
            { label: 'Heartthrob mirror ball heart', href: '/products/heartthrob-mirror-ball-heart-wall-art' },
            { label: 'Browse Disco Wall Art', href: '/collections/disco-wall-art' }
          ]
        },
        quiet: {
          title: 'A quieter glint',
          copy: 'Look at smaller reflective pieces and give them a little breathing room. Compare the current planter range before deciding where yours will sit.',
          links: [
            { label: 'Disco Ball Plant Pot', href: '/products/disco-ball-plant-pot' },
            { label: 'Browse Mirror Ball Planters', href: '/collections/disco-planters' },
            { label: 'Browse all Disco Decor', href: '/collections/disco' }
          ]
        },
        playful: {
          title: 'A conversation starter',
          copy: 'Choose the object that makes you smile first, then check its product details and the space you have for it.',
          links: [
            { label: 'Hanging Disco Ball Planter', href: '/products/hanging-disco-ball-planter' },
            { label: 'Afterparty Disco Barware', href: '/products/disco-delight-barware-set' },
            { label: 'Browse all Disco Decor', href: '/collections/disco' }
          ]
        }
      }
    },
    garden: {
      kicker: 'Garden ornaments',
      heading: 'Find your garden starting point',
      intro: 'Choose the mood and placement you have in mind, then compare a few current garden pieces.',
      questions: [
        {
          id: 'mood',
          label: 'What feeling should the piece bring?',
          options: [
            { id: 'playful', title: 'A little mischief', desc: 'Something visitors will notice' },
            { id: 'gentle', title: 'A quieter presence', desc: 'A softer garden detail' },
            { id: 'bold', title: 'A clear focal point', desc: 'A shape with visual weight' }
          ]
        },
        {
          id: 'place',
          label: 'Where are you looking?',
          options: [
            { id: 'border', title: 'Among plants', desc: 'A detail within a border' },
            { id: 'entrance', title: 'By a doorway', desc: 'A piece people meet first' },
            { id: 'open', title: 'An open focal spot', desc: 'Room for the form to read' }
          ]
        },
        {
          id: 'subject',
          label: 'Which subject would you enjoy seeing?',
          options: [
            { id: 'animal', title: 'An animal', desc: 'Character and recognition' },
            { id: 'shape', title: 'A playful shape', desc: 'A simple graphic silhouette' },
            { id: 'story', title: 'A small story', desc: 'A piece with a scene or gesture' }
          ]
        }
      ],
      outcomes: {
        playful: {
          title: 'A playful animal or shape',
          copy: 'Start with a characterful piece and check the product details for the finish and placement that suit your spot.',
          links: [
            { label: 'Capybara cast stone sculpture', href: '/products/capybara-orange-cast-stone-sculpture' },
            { label: 'Cheeky alligator sculpture', href: '/products/cheeky-alligator-cast-stone-sculpture' },
            { label: 'Browse Garden Ornaments', href: '/collections/garden-collection' }
          ]
        },
        gentle: {
          title: 'A quieter garden presence',
          copy: 'Look for a smaller form or a familiar animal, then decide where it will be seen without competing with the planting around it.',
          links: [
            { label: 'The Little Critic', href: '/products/the-little-critic' },
            { label: 'Handmade corgi ornament', href: '/products/handmade-corgi-stone-ornament-heart-detail' },
            { label: 'Browse Garden Ornaments', href: '/collections/garden-collection' }
          ]
        },
        bold: {
          title: 'A clear focal form',
          copy: 'Give the shape room around it and compare the current garden range before choosing the piece that earns the spot.',
          links: [
            { label: 'Stone balloon dog sculpture', href: '/products/stone-balloon-dog-sculpture' },
            { label: 'Browse Garden Ornaments', href: '/collections/garden-collection' },
            { label: 'Browse Home & Garden', href: '/collections/planters-home-decor' }
          ]
        }
      }
    },
    interior: {
      kicker: 'Sculptural home decor',
      heading: 'Find your interior starting point',
      intro: 'Choose the surface, shape and contrast you want to live with, then compare a few current pieces.',
      questions: [
        {
          id: 'surface',
          label: 'Where will it live?',
          options: [
            { id: 'shelf', title: 'Shelf or console', desc: 'A close-up object' },
            { id: 'table', title: 'Table or sideboard', desc: 'A piece at hand height' },
            { id: 'floor', title: 'A floor or larger spot', desc: 'Room for a stronger silhouette' }
          ]
        },
        {
          id: 'shape',
          label: 'Which shape draws you in?',
          options: [
            { id: 'portrait', title: 'A face or figure', desc: 'A sculptural portrait' },
            { id: 'round', title: 'A round reflective form', desc: 'Light and movement' },
            { id: 'graphic', title: 'A clear graphic shape', desc: 'A strong outline' }
          ]
        },
        {
          id: 'contrast',
          label: 'How should it sit with the room?',
          options: [
            { id: 'quiet', title: 'Calm and considered', desc: 'One object with space around it' },
            { id: 'reflective', title: 'Catch the light', desc: 'A surface that changes through the day' },
            { id: 'character', title: 'Bring some character', desc: 'A piece people ask about' }
          ]
        }
      ],
      outcomes: {
        portrait: {
          title: 'A portrait planter',
          copy: 'Compare the head and face planters by the shape and finish you prefer. Check each product page for its current details before ordering.',
          links: [
            { label: 'African Woman Head Planter', href: '/products/african-woman-head-planter' },
            { label: 'Harvest Goddess Planter', href: '/products/harvest-goddess-planter' },
            { label: 'Browse Head & Face Planters', href: '/collections/african-head-planters' }
          ]
        },
        round: {
          title: 'A reflective round form',
          copy: 'Use the light in the room as part of the decision. Compare the current disco planters and read the product details for the piece you like.',
          links: [
            { label: 'Disco Ball Plant Pot', href: '/products/disco-ball-plant-pot' },
            { label: 'Hanging Disco Ball Planter', href: '/products/hanging-disco-ball-planter' },
            { label: 'Browse Mirror Ball Planters', href: '/collections/disco-planters' }
          ]
        },
        graphic: {
          title: 'A graphic sculptural form',
          copy: 'Choose the silhouette that balances your room, then compare the current sculptural planter range for scale and finish.',
          links: [
            { label: 'David and Venus torso planters', href: '/products/david-venus-statue-planters' },
            { label: 'Greek God Male Planter', href: '/products/greek-god-male-planter' },
            { label: 'Browse Sculpted Planters', href: '/collections/sculpted-planters' }
          ]
        }
      }
    },
    sparkle: {
      kicker: 'Disco placement guide',
      heading: 'Find your sparkle starting point',
      intro: 'Choose the light and job you have in mind for a simple placement suggestion.',
      questions: [
        {
          id: 'light',
          label: 'What light will it meet?',
          options: [
            { id: 'daylight', title: 'Daylight', desc: 'A window or bright room' },
            { id: 'lamp', title: 'Lamp light', desc: 'A warmer evening glow' },
            { id: 'mixed', title: 'A mixture', desc: 'The room changes through the day' }
          ]
        },
        {
          id: 'job',
          label: 'What should it add?',
          options: [
            { id: 'glint', title: 'A small glint', desc: 'A detail you notice close up' },
            { id: 'focal', title: 'A focal point', desc: 'Something that leads the eye' },
            { id: 'conversation', title: 'A talking point', desc: 'A piece guests ask about' }
          ]
        },
        {
          id: 'surface',
          label: 'Which surface feels right?',
          options: [
            { id: 'wall', title: 'Wall', desc: 'A vertical focal point' },
            { id: 'shelf', title: 'Shelf or console', desc: 'A closer display' },
            { id: 'table', title: 'Table or bar', desc: 'Where people gather' }
          ]
        }
      ],
      outcomes: {
        glint: {
          title: 'A small reflective detail',
          copy: 'Start with a piece you can see at close range and let the room provide the rest of the atmosphere.',
          links: [
            { label: 'Disco Ball Plant Pot', href: '/products/disco-ball-plant-pot' },
            { label: 'Browse Mirror Ball Planters', href: '/collections/disco-planters' },
            { label: 'Browse all Disco Decor', href: '/collections/disco' }
          ]
        },
        focal: {
          title: 'A reflective focal point',
          copy: 'Give the piece a clear sightline and check the product page for the current size, price and details before deciding.',
          links: [
            { label: 'The Disco Era mirror ball bust', href: '/products/the-disco-era-mirror-ball-bust-wall-art' },
            { label: 'Browse Disco Wall Art', href: '/collections/disco-wall-art' },
            { label: 'Browse all Disco Decor', href: '/collections/disco' }
          ]
        },
        conversation: {
          title: 'A conversation piece',
          copy: 'Choose the object with the character you want in the room, then give it a useful position for guests to see.',
          links: [
            { label: 'Heartthrob mirror ball heart', href: '/products/heartthrob-mirror-ball-heart-wall-art' },
            { label: 'Afterparty Disco Barware', href: '/products/disco-delight-barware-set' },
            { label: 'Browse all Disco Decor', href: '/collections/disco' }
          ]
        }
      }
    },
    plant: {
      kicker: 'Plant pairing guide',
      heading: 'Choose a planter starting point',
      intro: 'Use three simple prompts to narrow the planter shape you want. Check the plant care label and inner fit before you buy.',
      questions: [
        {
          id: 'light',
          label: 'What do you already know about the spot?',
          options: [
            { id: 'bright', title: 'It is bright', desc: 'A window or well-lit room' },
            { id: 'gentle', title: 'It is gentler light', desc: 'Bright, but away from direct rays' },
            { id: 'unknown', title: 'I am still checking', desc: 'I will decide after measuring the spot' }
          ]
        },
        {
          id: 'care',
          label: 'What do you want to check first?',
          options: [
            { id: 'water', title: 'Watering rhythm', desc: 'Read the plant care instructions' },
            { id: 'shape', title: 'The plant outline', desc: 'Trailing, upright or compact' },
            { id: 'fit', title: 'Inner pot fit', desc: 'Check the measurements together' }
          ]
        },
        {
          id: 'outline',
          label: 'Which outline suits the piece?',
          options: [
            { id: 'trailing', title: 'Trailing', desc: 'Leaves that fall over the edge' },
            { id: 'upright', title: 'Upright', desc: 'A stronger vertical line' },
            { id: 'compact', title: 'Compact', desc: 'A smaller, contained shape' }
          ]
        }
      ],
      outcomes: {
        trailing: {
          title: 'A trailing contrast',
          copy: 'Compare planters with enough visual room for a trailing outline. Check the planter dimensions and the plant care label together before ordering.',
          links: [
            { label: 'Hanging Disco Ball Planter', href: '/products/hanging-disco-ball-planter' },
            { label: 'Disco Ball Plant Pot', href: '/products/disco-ball-plant-pot' },
            { label: 'Browse Mirror Ball Planters', href: '/collections/disco-planters' }
          ]
        },
        upright: {
          title: 'An upright silhouette',
          copy: 'A stronger vertical plant can sit against a sculptural pot. Check the plant care label and the planter measurements for the particular combination.',
          links: [
            { label: 'Harvest Goddess Planter', href: '/products/harvest-goddess-planter' },
            { label: 'African Woman Head Planter', href: '/products/african-woman-head-planter' },
            { label: 'Browse Sculpted Planters', href: '/collections/sculpted-planters' }
          ]
        },
        compact: {
          title: 'A compact pairing',
          copy: 'Start with a smaller plant and a clear silhouette. Confirm the inner fit and the plant care instructions before choosing the pot.',
          links: [
            { label: 'Disco Cube Planter', href: '/products/disco-cube-planter' },
            { label: 'Glam Goddess Mirror Planter', href: '/products/glam-goddess-mirror-planter' },
            { label: 'Browse Planters & Home Decor', href: '/collections/planters-home-decor' }
          ]
        }
      }
    }
  };

  var DREAM_ITEMS = [
    { title: 'Heartthrob mirror ball heart', href: '/products/heartthrob-mirror-ball-heart-wall-art' },
    { title: 'The Disco Era mirror ball bust', href: '/products/the-disco-era-mirror-ball-bust-wall-art' },
    { title: 'Hanging Disco Ball Planter', href: '/products/hanging-disco-ball-planter' },
    { title: 'Disco Ball Plant Pot', href: '/products/disco-ball-plant-pot' },
    { title: 'Afterparty Disco Barware', href: '/products/disco-delight-barware-set' },
    { title: 'Disco Ball Mannequin Bust', href: '/products/mirror-ball-mannequin-head' }
  ];

  // Featured-media readback from the dated Shopify product snapshot. This
  // map is generated from the exact product handles used above and contains
  // no synthetic or placeholder imagery. The source hash and capture time are
  // recorded beside this candidate in product-media-readback.json.
  var PRODUCT_MEDIA = {
    'african-woman-head-planter': {
      src: 'https://cdn.shopify.com/s/files/1/0834/5446/0252/files/43a798744156b158f0dc86b9200d666a.png?v=1775921064',
      alt: 'Golden Grace African Woman Head Planter with a plant'
    },
    'capybara-orange-cast-stone-sculpture': {
      src: 'https://cdn.shopify.com/s/files/1/0834/5446/0252/files/2_74ab1caf-2528-438c-9281-536dfdcadda3.png?v=1743374033',
      alt: 'Capybara with orange cast stone sculpture for garden display'
    },
    'cheeky-alligator-cast-stone-sculpture': {
      src: 'https://cdn.shopify.com/s/files/1/0834/5446/0252/files/2_7ca77a22-2fc1-4791-a855-7ea7278e64ca.png?v=1743373394',
      alt: 'Cast stone crocodile garden ornament held in one hand for scale'
    },
    'david-venus-statue-planters': {
      src: 'https://cdn.shopify.com/s/files/1/0834/5446/0252/files/Copy_of_IMAGES_-_Venus_and_David.png?v=1743369775',
      alt: 'David and Venus classical torso planters displayed together with spider plants'
    },
    'disco-ball-plant-pot': {
      src: 'https://cdn.shopify.com/s/files/1/0834/5446/0252/files/00-thumbnail-silver-pothos.png?v=1776863042',
      alt: 'Silver mirror mosaic cylinder plant pot with trailing green pothos on a purple backdrop'
    },
    'disco-cube-planter': {
      src: 'https://cdn.shopify.com/s/files/1/0834/5446/0252/files/hero-experiment-20260905-1694494333.png?v=1788624932',
      alt: 'Mirror tile disco cube planter with square drainage tray on a warm-grey tabletop'
    },
    'disco-delight-barware-set': {
      src: 'https://cdn.shopify.com/s/files/1/0834/5446/0252/files/afterparty-barware-hero.png?v=1776371951',
      alt: 'Three silver mirror mosaic cocktail glasses with colourful garnish details on a purple backdrop'
    },
    'glam-goddess-mirror-planter': {
      src: 'https://cdn.shopify.com/s/files/1/0834/5446/0252/files/8.png?v=1744478973',
      alt: 'Glam Goddess Mirror Planter - handmade mirror tile planter | Ripleys Nest'
    },
    'greek-god-male-planter': {
      src: 'https://cdn.shopify.com/s/files/1/0834/5446/0252/files/1_2bb61062-3180-40e4-b94e-7cf0955caee0.png?v=1743368759',
      alt: 'Large planted Greek god male torso planter beside a smaller unplanted planter'
    },
    'handmade-corgi-stone-ornament-heart-detail': {
      src: 'https://cdn.shopify.com/s/files/1/0834/5446/0252/files/9_8094b1df-e833-4892-baf5-6ce0957520b7.png?v=1743374277',
      alt: 'Handmade Corgi Stone Ornament with Heart - handmade cast stone garden ornament | Ripleys Nest'
    },
    'hanging-disco-ball-planter': {
      src: 'https://cdn.shopify.com/s/files/1/0834/5446/0252/files/glitter-ball-1.png?v=1776016136',
      alt: 'The Glitter Ball - Hanging Disco Ball Planter'
    },
    'harvest-goddess-planter': {
      src: 'https://cdn.shopify.com/s/files/1/0834/5446/0252/files/planted-spider-19.png?v=1775940356',
      alt: 'Harvest Goddess cast stone planter with spider plant against a black backdrop'
    },
    'heartthrob-mirror-ball-heart-wall-art': {
      src: 'https://cdn.shopify.com/s/files/1/0834/5446/0252/files/01-thumbnail-approved.png?v=1776930806',
      alt: 'Heartthrob mirror mosaic heart wall art'
    },
    'mirror-ball-mannequin-head': {
      src: 'https://cdn.shopify.com/s/files/1/0834/5446/0252/files/hero-experiment-20260905-1858225824.png?v=1788625423',
      alt: 'Disco Ball Mannequin Bust | Wig & Hat Stand'
    },
    'stone-balloon-dog-sculpture': {
      src: 'https://cdn.shopify.com/s/files/1/0834/5446/0252/files/8_761c6c59-022f-419e-93d1-8f91ad9c567d.png?v=1751826557',
      alt: 'Balloon Dog Sculpture - handmade cast stone garden ornament | Ripleys Nest'
    },
    'the-disco-era-mirror-ball-bust-wall-art': {
      src: 'https://cdn.shopify.com/s/files/1/0834/5446/0252/files/disco-era-1.png?v=1776168493',
      alt: 'Mirror ball bust wall art with glass mirror tiles for disco wall decor'
    },
    'the-little-critic': {
      src: 'https://cdn.shopify.com/s/files/1/0834/5446/0252/files/hero-experiment-20260905-4521301265.png?v=1788625393',
      alt: 'The Little Critic Owl Garden Ornament'
    }
  };

  function make(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function makeControl(tag, className, text) {
    var node = make(tag, className);
    node.appendChild(make('span', 'btn__text', text));
    return node;
  }

  function link(label, href, className) {
    var anchor = make('a', className || 'btn btn--secondary rn-discovery__result-link');
    anchor.href = href;
    anchor.appendChild(make('span', 'btn__text', label));
    return anchor;
  }

  function productHandleFromHref(href) {
    var match = String(href || '').match(/^\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  function productMedia(item, eager) {
    var handle = productHandleFromHref(item && item.href);
    var media = PRODUCT_MEDIA[handle];
    if (!media) return null;
    var stage = make('div', 'media-wrapper rn-discovery__card-media');
    stage.setAttribute('data-rn-discovery-media-handle', handle);
    var image = make('img');
    var imageUrl = new URL(media.src);
    imageUrl.searchParams.set('width', '720');
    image.src = imageUrl.href;
    image.width = 720;
    image.height = 720;
    image.alt = media.alt || item.label || item.title || '';
    image.loading = eager ? 'eager' : 'lazy';
    image.decoding = 'async';
    stage.appendChild(image);
    return stage;
  }

  function setHeader(root, mode) {
    var config = MODES[mode];
    root.querySelector('[data-rn-discovery-kicker]').textContent = config.kicker;
    root.querySelector('[data-rn-discovery-heading]').textContent = config.heading;
    root.querySelector('[data-rn-discovery-intro]').textContent = config.intro;
  }

  function renderQuestion(root, config, index, answers, shouldFocus) {
    var panel = root.querySelector('[data-rn-discovery-panel]');
    var question = config.questions[index];
    var fieldset = make('fieldset', 'rn-discovery__question');
    var legend = make('legend', 'h2', question.label);
    legend.setAttribute('tabindex', '-1');
    fieldset.appendChild(legend);
    var progress = make('p', 'rn-discovery__progress', 'Question ' + (index + 1) + ' of ' + config.questions.length);
    progress.id = 'rn-discovery-progress-' + index;
    progress.setAttribute('role', 'status');
    progress.setAttribute('aria-live', 'polite');
    progress.setAttribute('aria-atomic', 'true');
    fieldset.setAttribute('aria-describedby', progress.id);
    fieldset.appendChild(progress);
    var options = make('div', 'rn-discovery__options');
    options.setAttribute('role', 'group');
    options.setAttribute('aria-label', question.label);
    question.options.forEach(function (option) {
      var selected = answers[question.id] === option.id;
      var button = make('button', 'btn btn--secondary rn-discovery__option' + (selected ? ' btn--active' : ''));
      button.type = 'button';
      button.setAttribute('data-rn-discovery-option', option.id);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      var buttonText = make('span', 'btn__text');
      buttonText.appendChild(make('strong', 'rn-discovery__option-title', option.title));
      buttonText.appendChild(make('span', 'rn-discovery__option-desc', option.desc));
      button.appendChild(buttonText);
      options.appendChild(button);
    });
    fieldset.appendChild(options);

    var actions = make('div', 'rn-discovery__question-actions');
    if (index > 0) {
      var back = makeControl('button', 'btn btn--secondary rn-discovery__back', 'Back');
      back.type = 'button';
      back.setAttribute('data-rn-discovery-back', '');
      actions.appendChild(back);
    }
    var next = makeControl('button', 'btn btn--primary rn-discovery__next', index === config.questions.length - 1 ? 'See my starting point' : 'Next');
    next.type = 'button';
    next.setAttribute('data-rn-discovery-next', '');
    actions.appendChild(next);
    actions.appendChild(make('p', '', 'Choose one option to continue.'));
    fieldset.appendChild(actions);
    panel.replaceChildren(fieldset);
    if (shouldFocus) {
      legend.focus();
      legend.scrollIntoView({ block: 'center', inline: 'nearest' });
    }
  }

  function homeLink(label, href) {
    return { label: label, href: href };
  }

  function recommendation(title, copy, links) {
    return { title: title, copy: copy, links: links };
  }

  function discoRecommendation(answers) {
    var mood = answers.mood || 'statement';
    var job = answers.job || 'wall';
    var place = answers.place || 'living';
    var placeText = {
      living: 'the living room',
      shelf: 'a shelf or console',
      bar: 'a kitchen or bar area'
    }[place] || 'your room';
    var collectionByPlace = {
      living: homeLink('Browse Disco Wall Art', '/collections/disco-wall-art'),
      shelf: homeLink('Browse all Disco Decor', '/collections/disco'),
      bar: homeLink('Browse all Disco Decor', '/collections/disco')
    };
    var collection = collectionByPlace[place];

    if (job === 'wall') {
      var wallPrimary = mood === 'quiet'
        ? homeLink('Heartthrob mirror ball heart', '/products/heartthrob-mirror-ball-heart-wall-art')
        : homeLink('The Disco Era mirror ball bust', '/products/the-disco-era-mirror-ball-bust-wall-art');
      var wallSecondary = mood === 'playful'
        ? homeLink('Heartthrob mirror ball heart', '/products/heartthrob-mirror-ball-heart-wall-art')
        : mood === 'quiet'
          ? homeLink('The Disco Era mirror ball bust', '/products/the-disco-era-mirror-ball-bust-wall-art')
          : homeLink('Heartthrob mirror ball heart', '/products/heartthrob-mirror-ball-heart-wall-art');
      return recommendation(
        mood === 'quiet' ? 'A quieter wall glint' : mood === 'playful' ? 'A wall piece guests will ask about' : 'A reflective wall focal point',
        'You want a ' + (mood === 'quiet' ? 'small glint' : mood === 'playful' ? 'conversation piece' : 'strong focal point') + ' for ' + placeText + '. Compare these wall pieces and check the product details for hanging and scale.',
        [wallPrimary, wallSecondary, collection]
      );
    }

    if (job === 'plant') {
      var plantPrimary = mood === 'quiet'
        ? homeLink('Hanging Disco Ball Planter', '/products/hanging-disco-ball-planter')
        : homeLink('Disco Ball Plant Pot', '/products/disco-ball-plant-pot');
      var plantSecondary = mood === 'quiet'
        ? homeLink('Disco Ball Plant Pot', '/products/disco-ball-plant-pot')
        : homeLink('Hanging Disco Ball Planter', '/products/hanging-disco-ball-planter');
      var plantCollection = place === 'shelf'
        ? homeLink('Browse Mirror Ball Planters', '/collections/disco-planters')
        : collection;
      return recommendation(
        mood === 'quiet' ? 'A little light around a plant' : mood === 'playful' ? 'A planter with party energy' : 'A reflective planter focal point',
        'You want reflective detail around a plant in ' + placeText + '. Compare the pot and hanging option, then check the inner fit, drainage choice and display guidance.',
        [plantPrimary, plantSecondary, plantCollection]
      );
    }

    var partyPrimary = mood === 'quiet'
      ? homeLink('Disco Ball Plant Pot', '/products/disco-ball-plant-pot')
      : mood === 'playful'
        ? homeLink('Afterparty Disco Barware', '/products/disco-delight-barware-set')
        : homeLink('Heartthrob mirror ball heart', '/products/heartthrob-mirror-ball-heart-wall-art');
    var partySecondary = mood === 'playful'
      ? homeLink('Heartthrob mirror ball heart', '/products/heartthrob-mirror-ball-heart-wall-art')
      : homeLink('Afterparty Disco Barware', '/products/disco-delight-barware-set');
    var partyCollection = place === 'bar'
      ? homeLink('Browse all Disco Decor', '/collections/disco')
      : place === 'shelf'
        ? homeLink('Browse Mirror Ball Planters', '/collections/disco-planters')
        : homeLink('Browse Disco Wall Art', '/collections/disco-wall-art');
    return recommendation(
      mood === 'quiet' ? 'A softer hosting detail' : mood === 'playful' ? 'A conversation piece for hosting' : 'A bold piece for hosting',
      'You want the disco detail to join people in ' + placeText + '. Start with these options, then check the product page for the space, finish and care details.',
      [partyPrimary, partySecondary, partyCollection]
    );
  }

  function gardenRecommendation(answers) {
    var mood = answers.mood || 'playful';
    var place = answers.place || 'border';
    var subject = answers.subject || 'animal';
    var moodText = { playful: 'playful', gentle: 'quieter', bold: 'stronger' }[mood] || 'distinctive';
    var placeText = { border: 'among plants', entrance: 'by a doorway', open: 'in an open focal spot' }[place] || 'in your garden';
    var primary;
    var secondary;
    if (subject === 'shape') {
      primary = mood === 'bold'
        ? homeLink('Stone balloon dog sculpture', '/products/stone-balloon-dog-sculpture')
        : homeLink('Cheeky alligator sculpture', '/products/cheeky-alligator-cast-stone-sculpture');
      secondary = mood === 'gentle'
        ? homeLink('The Little Critic', '/products/the-little-critic')
        : homeLink('Capybara cast stone sculpture', '/products/capybara-orange-cast-stone-sculpture');
    } else if (subject === 'story') {
      primary = mood === 'bold'
        ? homeLink('Capybara cast stone sculpture', '/products/capybara-orange-cast-stone-sculpture')
        : homeLink('The Little Critic', '/products/the-little-critic');
      secondary = mood === 'playful'
        ? homeLink('Cheeky alligator sculpture', '/products/cheeky-alligator-cast-stone-sculpture')
        : homeLink('Handmade corgi ornament', '/products/handmade-corgi-stone-ornament-heart-detail');
    } else {
      primary = mood === 'gentle'
        ? homeLink('The Little Critic', '/products/the-little-critic')
        : mood === 'bold'
          ? homeLink('Capybara cast stone sculpture', '/products/capybara-orange-cast-stone-sculpture')
          : homeLink('Cheeky alligator sculpture', '/products/cheeky-alligator-cast-stone-sculpture');
      secondary = mood === 'bold'
        ? homeLink('Cheeky alligator sculpture', '/products/cheeky-alligator-cast-stone-sculpture')
        : homeLink('Handmade corgi ornament', '/products/handmade-corgi-stone-ornament-heart-detail');
    }
    var gardenCollection = place === 'border'
      ? homeLink('Browse Home & Garden', '/collections/planters-home-decor')
      : homeLink('Browse Garden Ornaments', '/collections/garden-collection');
    return recommendation(
      'A ' + moodText + ' garden ' + (subject === 'animal' ? 'animal' : subject),
      'You are looking for a ' + moodText + ' ' + (subject === 'animal' ? 'recognisable animal' : subject) + ' for ' + placeText + '. Compare the finish, scale and outdoor guidance on each product page.',
      [primary, secondary, gardenCollection]
    );
  }

  function interiorRecommendation(answers) {
    var surface = answers.surface || 'shelf';
    var shape = answers.shape || 'portrait';
    var contrast = answers.contrast || 'quiet';
    var surfaceText = { shelf: 'a shelf or console', table: 'a table or sideboard', floor: 'a larger floor spot' }[surface] || 'the room';
    var contrastText = { quiet: 'calm and considered', reflective: 'light catching', character: 'full of character' }[contrast] || 'right for the room';
    var primary;
    var secondary;
    var collection;
    if (shape === 'round') {
      primary = contrast === 'reflective'
        ? homeLink('Disco Ball Plant Pot', '/products/disco-ball-plant-pot')
        : homeLink('Hanging Disco Ball Planter', '/products/hanging-disco-ball-planter');
      secondary = surface === 'shelf'
        ? homeLink('Disco Cube Planter', '/products/disco-cube-planter')
        : contrast === 'reflective'
          ? homeLink('Hanging Disco Ball Planter', '/products/hanging-disco-ball-planter')
          : homeLink('Disco Ball Plant Pot', '/products/disco-ball-plant-pot');
      collection = contrast === 'quiet'
        ? homeLink('Browse Mirror Ball Planters', '/collections/disco-planters')
        : homeLink('Browse all Disco Decor', '/collections/disco');
    } else if (shape === 'graphic') {
      primary = surface === 'floor'
        ? homeLink('David and Venus torso planters', '/products/david-venus-statue-planters')
        : homeLink('Greek God Male Planter', '/products/greek-god-male-planter');
      secondary = contrast === 'character'
        ? surface === 'floor'
          ? homeLink('Greek God Male Planter', '/products/greek-god-male-planter')
          : homeLink('David and Venus torso planters', '/products/david-venus-statue-planters')
        : homeLink('Disco Cube Planter', '/products/disco-cube-planter');
      collection = contrast === 'reflective'
        ? homeLink('Browse Sculpted Planters', '/collections/sculpted-planters')
        : homeLink('Browse Planters & Home Decor', '/collections/planters-home-decor');
    } else {
      primary = surface === 'table'
        ? homeLink('Harvest Goddess Planter', '/products/harvest-goddess-planter')
        : homeLink('African Woman Head Planter', '/products/african-woman-head-planter');
      secondary = contrast === 'character'
        ? surface === 'table'
          ? homeLink('Glam Goddess Mirror Planter', '/products/glam-goddess-mirror-planter')
          : homeLink('Harvest Goddess Planter', '/products/harvest-goddess-planter')
        : surface === 'table'
          ? homeLink('African Woman Head Planter', '/products/african-woman-head-planter')
          : homeLink('Glam Goddess Mirror Planter', '/products/glam-goddess-mirror-planter');
      collection = contrast === 'reflective'
        ? homeLink('Browse Sculpted Planters', '/collections/sculpted-planters')
        : homeLink('Browse Head & Face Planters', '/collections/african-head-planters');
    }
    return recommendation(
      shape === 'portrait' ? 'A portrait planter' : shape === 'round' ? 'A reflective round form' : 'A graphic sculptural form',
      'You want ' + contrastText + ' detail on ' + surfaceText + '. Compare these current pieces, then check the product page for scale, finish and plant fit.',
      [primary, secondary, collection]
    );
  }

  function sparkleRecommendation(answers) {
    var light = answers.light || 'daylight';
    var job = answers.job || 'glint';
    var surface = answers.surface || 'wall';
    var lightText = { daylight: 'daylight', lamp: 'warmer lamp light', mixed: 'changing light' }[light] || 'the light in the room';
    var surfaceText = { wall: 'a wall', shelf: 'a shelf or console', table: 'a table or bar' }[surface] || 'the room';
    var primary;
    var secondary;
    if (surface === 'wall') {
      primary = job === 'focal'
        ? homeLink('The Disco Era mirror ball bust', '/products/the-disco-era-mirror-ball-bust-wall-art')
        : homeLink('Heartthrob mirror ball heart', '/products/heartthrob-mirror-ball-heart-wall-art');
      secondary = job === 'focal'
        ? homeLink('Heartthrob mirror ball heart', '/products/heartthrob-mirror-ball-heart-wall-art')
        : homeLink('The Disco Era mirror ball bust', '/products/the-disco-era-mirror-ball-bust-wall-art');
    } else {
      primary = job === 'conversation' && surface === 'table'
        ? homeLink('Afterparty Disco Barware', '/products/disco-delight-barware-set')
        : homeLink('Disco Ball Plant Pot', '/products/disco-ball-plant-pot');
      secondary = surface === 'shelf'
        ? homeLink('Browse all Disco Decor', '/collections/disco')
        : homeLink('Browse Mirror Ball Planters', '/collections/disco-planters');
    }
    var collection = surface === 'wall'
      ? homeLink('Browse Disco Wall Art', '/collections/disco-wall-art')
      : surface === 'shelf'
        ? homeLink('Browse Mirror Ball Planters', '/collections/disco-planters')
        : homeLink('Browse all Disco Decor', '/collections/disco');
    return recommendation(
      job === 'glint' ? 'A small reflective detail' : job === 'focal' ? 'A reflective focal point' : 'A conversation piece',
      'You want ' + (job === 'glint' ? 'a small glint' : job === 'focal' ? 'a clear focal point' : 'a talking point') + ' under ' + lightText + ' on ' + surfaceText + '. ' + (light === 'daylight' ? 'Look at how daylight reaches that spot across the day.' : light === 'lamp' ? 'Check where your lamp falls on the piece; reflected colour depends on the bulb and room.' : 'Compare the spot in daylight and with the lamps you normally use.') + ' Check the product dimensions and ' + (surface === 'wall' ? 'wall fittings' : 'base and display requirements') + ' before choosing.',
      [primary, secondary, collection]
    );
  }

  function plantRecommendation(answers) {
    var light = answers.light || 'bright';
    var care = answers.care || 'water';
    var outline = answers.outline || 'trailing';
    var lightText = { bright: 'a bright spot', gentle: 'gentler light', unknown: 'a spot you are still checking' }[light] || 'the room';
    var careText = { water: 'watering rhythm', shape: 'plant outline', fit: 'inner pot fit' }[care] || 'the plant details';
    var primary;
    var secondary;
    if (outline === 'upright') {
      primary = light === 'gentle'
        ? homeLink('African Woman Head Planter', '/products/african-woman-head-planter')
        : homeLink('Harvest Goddess Planter', '/products/harvest-goddess-planter');
      secondary = care === 'shape'
        ? homeLink('Glam Goddess Mirror Planter', '/products/glam-goddess-mirror-planter')
        : light === 'gentle'
          ? homeLink('Harvest Goddess Planter', '/products/harvest-goddess-planter')
          : homeLink('African Woman Head Planter', '/products/african-woman-head-planter');
    } else if (outline === 'compact') {
      primary = light === 'gentle'
        ? homeLink('Glam Goddess Mirror Planter', '/products/glam-goddess-mirror-planter')
        : homeLink('Disco Cube Planter', '/products/disco-cube-planter');
      secondary = care === 'shape'
        ? homeLink('Harvest Goddess Planter', '/products/harvest-goddess-planter')
        : light === 'gentle'
          ? homeLink('Disco Cube Planter', '/products/disco-cube-planter')
          : homeLink('Glam Goddess Mirror Planter', '/products/glam-goddess-mirror-planter');
    } else {
      primary = light === 'gentle'
        ? homeLink('Disco Ball Plant Pot', '/products/disco-ball-plant-pot')
        : homeLink('Hanging Disco Ball Planter', '/products/hanging-disco-ball-planter');
      secondary = care === 'shape'
        ? homeLink('Disco Cube Planter', '/products/disco-cube-planter')
        : light === 'gentle'
          ? homeLink('Hanging Disco Ball Planter', '/products/hanging-disco-ball-planter')
          : homeLink('Disco Ball Plant Pot', '/products/disco-ball-plant-pot');
    }
    var collection = care === 'shape'
      ? homeLink('Browse Sculpted Planters', '/collections/sculpted-planters')
      : care === 'fit'
        ? homeLink('Browse Planters & Home Decor', '/collections/planters-home-decor')
        : homeLink('Browse Mirror Ball Planters', '/collections/disco-planters');
    return recommendation(
      outline === 'trailing' ? 'A trailing contrast' : outline === 'upright' ? 'An upright silhouette' : 'A compact pairing',
      'You are pairing a ' + outline + ' plant with ' + lightText + ' and want to check the ' + careText + ' first. Compare these planters, then confirm the inner fit and care label before ordering.',
      [primary, secondary, collection]
    );
  }

  function recommendationFor(config, answers) {
    if (config === MODES.disco) return discoRecommendation(answers);
    if (config === MODES.garden) return gardenRecommendation(answers);
    if (config === MODES.interior) return interiorRecommendation(answers);
    if (config === MODES.sparkle) return sparkleRecommendation(answers);
    if (config === MODES.plant) return plantRecommendation(answers);
    var keys = Object.keys(config.outcomes);
    return config.outcomes[keys[0]];
  }

  function renderResult(root, config, answers) {
    var panel = root.querySelector('[data-rn-discovery-panel]');
    var result = recommendationFor(config, answers);
    var wrap = make('div', 'rn-discovery__result');
    var resultHeading = make('h2', 'h2 rn-discovery__result-heading', result.title);
    resultHeading.setAttribute('tabindex', '-1');
    wrap.appendChild(resultHeading);
    wrap.appendChild(make('p', 'rte rn-discovery__result-copy', result.copy));
    var links = make('ul', 'rn-discovery__links');
    result.links.forEach(function (item, index) {
      var li = make('li');
      var media = productMedia(item, index < 2);
      if (media) li.appendChild(media);
      li.appendChild(link(item.label, item.href));
      links.appendChild(li);
    });
    wrap.appendChild(links);
    wrap.appendChild(make('p', 'rn-discovery__result-note', 'Open a product page for its current details, price, availability and delivery information.'));
    var actions = make('div', 'rn-discovery__result-actions');
    var restart = makeControl('button', 'btn btn--secondary rn-discovery__restart', 'Start again');
    restart.type = 'button';
    restart.setAttribute('data-rn-discovery-restart', '');
    actions.appendChild(restart);
    wrap.appendChild(actions);
    panel.replaceChildren(wrap);
    resultHeading.focus();
    resultHeading.scrollIntoView({ block: 'center', inline: 'nearest' });
  }

  function renderDream(root) {
    var panel = root.querySelector('[data-rn-discovery-panel]');
    var wrap = make('div', 'rn-discovery__result');
    wrap.appendChild(make('h2', 'h2 focus-none rn-discovery__result-heading', 'Build a short list'));
    wrap.appendChild(make('p', 'rte rn-discovery__result-copy', 'Choose up to three pieces to compare. Your shortlist stays on this page only.'));
    var status = make('p', 'rn-discovery__shortlist-status', '0 of 3 selected');
    status.setAttribute('data-rn-discovery-shortlist-status', '');
    wrap.appendChild(status);
    var cards = make('ul', 'rn-discovery__cards');
    DREAM_ITEMS.forEach(function (item, index) {
      var li = make('li', 'rn-discovery__card');
      li.setAttribute('data-rn-discovery-card', item.href);
      li.setAttribute('data-selected', 'false');
      var media = productMedia(item, index < 2);
      if (media) li.appendChild(media);
      li.appendChild(make('strong', 'rn-discovery__card-title', item.title));
      li.appendChild(link('Open product details', item.href, 'btn btn--link rn-discovery__card-link'));
      var toggle = makeControl('button', 'btn btn--secondary rn-discovery__card-toggle', 'Add to shortlist');
      toggle.type = 'button';
      toggle.setAttribute('data-rn-discovery-toggle', '');
      toggle.setAttribute('aria-pressed', 'false');
      li.appendChild(toggle);
      cards.appendChild(li);
    });
    wrap.appendChild(cards);
    var actions = make('div', 'rn-discovery__result-actions');
    var browse = make('a', 'btn btn--primary rn-discovery__browse');
    browse.href = '/collections/disco';
    browse.appendChild(make('span', 'btn__text', 'Browse all Disco Decor'));
    actions.appendChild(browse);
    wrap.appendChild(actions);
    panel.replaceChildren(wrap);
  }

  function initDream(root) {
    var selected = [];
    renderDream(root);
    root.addEventListener('click', function (event) {
      var toggle = event.target.closest('[data-rn-discovery-toggle]');
      if (!toggle) return;
      var card = toggle.closest('[data-rn-discovery-card]');
      var href = card.getAttribute('data-rn-discovery-card');
      var index = selected.indexOf(href);
      if (index === -1) {
        if (selected.length >= 3) {
          root.querySelector('[data-rn-discovery-shortlist-status]').textContent = 'Choose up to three pieces.';
          return;
        }
        selected.push(href);
      } else {
        selected.splice(index, 1);
      }
      var isSelected = selected.indexOf(href) !== -1;
      card.setAttribute('data-selected', String(isSelected));
      toggle.setAttribute('aria-pressed', String(isSelected));
      toggle.classList.toggle('btn--active', isSelected);
      toggle.querySelector('.btn__text').textContent = isSelected ? 'Remove from shortlist' : 'Add to shortlist';
      root.querySelector('[data-rn-discovery-shortlist-status]').textContent = selected.length + ' of 3 selected';
    });
  }

  function init(root) {
    var mode = root.getAttribute('data-rn-discovery-mode') || 'disco';
    if (mode === 'dream') {
      root.querySelector('[data-rn-discovery-kicker]').textContent = 'Disco decor';
      root.querySelector('[data-rn-discovery-heading]').textContent = 'Build your disco shortlist';
      root.querySelector('[data-rn-discovery-intro]').textContent = 'Choose a few current pieces to compare. Your shortlist stays on this page only.';
      initDream(root);
      return;
    }
    var config = MODES[mode] || MODES.disco;
    setHeader(root, mode);
    var state = { index: 0, answers: {} };
    renderQuestion(root, config, state.index, state.answers, false);

    root.addEventListener('click', function (event) {
      var option = event.target.closest('[data-rn-discovery-option]');
      if (option) {
        var current = config.questions[state.index];
        state.answers[current.id] = option.getAttribute('data-rn-discovery-option');
        root.querySelectorAll('[data-rn-discovery-option]').forEach(function (node) {
          node.setAttribute('aria-pressed', node === option ? 'true' : 'false');
          node.classList.toggle('btn--active', node === option);
        });
        return;
      }
      var next = event.target.closest('[data-rn-discovery-next]');
      if (next) {
        var question = config.questions[state.index];
        if (!state.answers[question.id]) {
          var note = root.querySelector('.rn-discovery__question-actions p');
          if (note) note.textContent = 'Choose one option before continuing.';
          return;
        }
        if (state.index === config.questions.length - 1) {
          renderResult(root, config, state.answers);
        } else {
          state.index += 1;
          renderQuestion(root, config, state.index, state.answers, true);
        }
        return;
      }
      if (event.target.closest('[data-rn-discovery-back]')) {
        if (state.index > 0) {
          state.index -= 1;
          renderQuestion(root, config, state.index, state.answers, true);
        }
        return;
      }
      if (event.target.closest('[data-rn-discovery-restart]')) {
        state.index = 0;
        state.answers = {};
        renderQuestion(root, config, state.index, state.answers, true);
      }
    });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-rn-home-discovery]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); }, { once: true });
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
}());
